import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift } from "lucide-react";
import LeadForm from "./LeadForm";

const POPUP_KEY = "leadPopupShown";

export default function LeadPopup() {
  const [isOpen, setIsOpen] = useState(false);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(POPUP_KEY, "true");
  }, []);

  useEffect(() => {
    if (localStorage.getItem(POPUP_KEY)) return;

    const timer = setTimeout(() => {
      if (!localStorage.getItem(POPUP_KEY)) {
        setIsOpen(true);
        localStorage.setItem(POPUP_KEY, "true");
      }
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  // Exit intent — re-check localStorage inside callback
  useEffect(() => {
    if (localStorage.getItem(POPUP_KEY)) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !localStorage.getItem(POPUP_KEY)) {
        setIsOpen(true);
        localStorage.setItem(POPUP_KEY, "true");
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/60 backdrop-blur-sm"
        onClick={dismiss}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-card rounded-lg shadow-luxury-lg max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="bg-primary p-6 text-primary-foreground relative">
            <button
              onClick={dismiss}
              aria-label="Close popup"
              className="absolute top-4 right-4 min-w-[44px] min-h-[44px] rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                <Gift className="w-6 h-6 text-accent-foreground" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold">
                  Exclusive Offer!
                </h3>
                <p className="text-primary-foreground/70 text-sm">
                  Limited Time Only
                </p>
              </div>
            </div>
            <p className="text-primary-foreground/90 text-sm leading-relaxed">
              Get exclusive property deals before public listing + Free site
              visit with our expert consultant.
            </p>
          </div>

          {/* Form */}
          <div className="p-6">
            <LeadForm
              source="popup"
              onSuccess={dismiss}
              compact
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
